import { ServerListRequest } from '@lumapps/base-api';
import { TranslatableObject, TranslateObject } from '@lumapps/translations';
import type { Fields } from '@lumapps/utils/types/Fields';

import type { InstanceListMetadataRequest, ListMetadataRequest } from './api';

export enum MetadataEntity {
    content = 'CONTENT',
    community = 'COMMUNITY',
    all = 'ALL',
}

export interface Metadata {
    uid: string;
    id: string;
    name: TranslateObject<string>;
    description?: TranslateObject<string>;
    parent?: string;
    familyKey: string;
    heritable: boolean;
    customer: string;
    sortOrder: string;
    multiple: boolean;
    customContentTypes: string[];
    isVisibleFront: boolean;
    functionalInnerId: string;
    instance?: string;
    createdAt: string;
    updatedAt: string;
    values?: string;
    displayInFilter: boolean;
    level?: number;
    canBeAppliedTo?: MetadataEntity[];
}

export type MetadataByID = Record<string, Metadata>;
export type MetadataByParent = Record<Metadata['id'], Array<Metadata>>;

export interface MetadataV2 {
    id: string;
    // Family id
    rootId?: string;
    // Direct parent id
    parentId?: string;
    name: string;
    rootName?: string;
    description?: string;
}

export type MetadataListV2 = MetadataV2[];

/** Translated Metadata type means that name and description are translated in every languages available */
export interface TranslatedMetadataV2 {
    id: string;
    rootId?: string;
    parentId?: string;
    name: TranslatableObject;
    description?: TranslatableObject;
    root?: TranslatedMetadataV2;
    parent?: TranslatedMetadataV2;
}

export type TranslatedMetadataListV2 = TranslatedMetadataV2[];

export interface MetadataListRequestParams extends ServerListRequest {
    instance?: string;
    customerId?: string;
    emptyParent?: boolean;
    more?: boolean;
    canBeAppliedTo?: MetadataEntity[];
}

export interface QueryMetadataOptions {
    enabled?: boolean;
    fields?: Fields<Metadata>;
}

/** Sub-set type of {@link Metadata} required to create a new metadata. */
export type NewMetadataPayload = {
    name: TranslateObject<string>;
    description: TranslateObject<string>;
    multiple?: boolean;
    customContentTypes?: string[];
    isVisibleFront?: boolean;
    instance?: string;
    customer: string;
};

/** Instance or customer metadata list options */
export type QueryMetadataListOptions =
    | ({ source: 'instance'; fields?: Fields<Metadata> } & Omit<Partial<InstanceListMetadataRequest>, 'fields'>)
    | ({ source: 'customer'; fields?: Fields<Metadata> } & Omit<ListMetadataRequest, 'fields'>);
